import { loadAuthUser } from './auth';
import { getUserStorageKey } from '../utils/storage';
import type {
  CheckInListItem,
  ManualCheckInPayload,
  ManualCheckInResult,
} from './exploreService';

const STORAGE_PREFIX = 'lumi_check_ins_month_v1';

function storageKey(year: number, month: number): string {
  const user = loadAuthUser();
  return getUserStorageKey(`${STORAGE_PREFIX}_${year}_${month}`, user?.id);
}

/** 从 local_date "YYYY-MM-DD" 取年、月 */
export function yearMonthFromLocalDate(localDate: string): { year: number; month: number } {
  const [y, m] = localDate.split('-').map(Number);
  return { year: y || new Date().getFullYear(), month: m || new Date().getMonth() + 1 };
}

export function loadCachedCheckInList(year: number, month: number): CheckInListItem[] {
  try {
    const raw = localStorage.getItem(storageKey(year, month));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean) as CheckInListItem[];
  } catch {
    return [];
  }
}

export function saveCachedCheckInList(
  year: number,
  month: number,
  items: CheckInListItem[],
): void {
  try {
    localStorage.setItem(storageKey(year, month), JSON.stringify(items));
  } catch {
    /* quota / private mode */
  }
}

/**
 * 合并缓存与接口：同 id 以 API 为准（服务端为准），仅存在于缓存的 id 保留（刚写入、list 尚未返回时）
 */
export function mergeCheckInApiWithCache(
  apiItems: CheckInListItem[],
  cachedItems: CheckInListItem[],
): CheckInListItem[] {
  const map = new Map<string, CheckInListItem>();
  for (const x of cachedItems) {
    if (x?.id) map.set(x.id, x);
  }
  for (const x of apiItems) {
    if (x?.id) map.set(x.id, x);
  }
  return Array.from(map.values()).sort((a, b) => b.created_at - a.created_at);
}

export function buildCheckInListItemFromManualSubmit(
  payload: ManualCheckInPayload,
  result: ManualCheckInResult,
): CheckInListItem {
  return {
    id: result.id,
    type: 'manual_entry',
    emotion_tag: payload.emotion_tag,
    emotion_category: result.emotion_category ?? null,
    emoji: result.emoji ?? '✨',
    color: result.color ?? '#6366F1',
    activity_tag: payload.activity_tag ?? null,
    activity_emoji: payload.activity_emoji ?? null,
    journal_title: payload.journal_title ?? null,
    journal_text: payload.journal_text ?? null,
    detected_emotions: null,
    persona_id: null,
    local_date: result.local_date,
    created_at: Date.now(),
    source_conversation_id: null,
  };
}

/** 手动签到提交成功后写入当月缓存，保证回到 Journal 立刻可见 */
export function cacheManualCheckInAfterSubmit(
  payload: ManualCheckInPayload,
  result: ManualCheckInResult,
): void {
  const { year, month } = yearMonthFromLocalDate(result.local_date);
  const newItem = buildCheckInListItemFromManualSubmit(payload, result);
  const prev = loadCachedCheckInList(year, month);
  const withoutDup = prev.filter((x) => x.id !== newItem.id);
  const next = mergeCheckInApiWithCache([newItem], withoutDup);
  saveCachedCheckInList(year, month, next);
}
